const LOG = '[growi-plugin-image-size]';
const PROCESSED_ATTR = 'data-growi-plugin-image-size';

// 走査対象 (GROWI のバージョン差異吸収)。本文以外も拾えるよう main を含めるが、
// 通常 img しか触らないので副作用は小さい。
const SCAN_ROOT_SELECTORS = [
  '.main',
  'main',
  '[data-testid="page-view"]',
  'body',
];

// 長さの正規表現。素の数値は px として扱う。
const LEN_RE_SRC = '\\d+(?:\\.\\d+)?(?:px|em|rem|%|vh|vw)?';

// alt 末尾のサイズ指定: " |300x200" / " =300x200" / " |300" / " |x200" など。
//   group1: 残りの alt
//   group2: 幅 (省略可)
//   group3: 高さ (省略可)
// 幅か高さの少なくとも一方が必要 (パース後にチェック)。
const SIZE_RE = new RegExp(
  '^(.*?)\\s*[|=]\\s*' +
    `(${LEN_RE_SRC})?` +
    `(?:x(${LEN_RE_SRC}))?` +
    '\\s*$',
);

interface ParsedAlt {
  cleanAlt: string;
  width: string | null;
  height: string | null;
}

const normalizeLength = (raw: string | undefined): string | null => {
  if (raw == null || raw === '') return null;
  // 単位なしの数値は px とみなす
  return /^\d+(?:\.\d+)?$/.test(raw) ? `${raw}px` : raw;
};

export const parseAlt = (alt: string): ParsedAlt | null => {
  const m = SIZE_RE.exec(alt);
  if (m == null) return null;
  const width = normalizeLength(m[2]);
  const height = normalizeLength(m[3]);
  if (width == null && height == null) return null;
  return { cleanAlt: m[1].trim(), width, height };
};

const applyToImage = (img: HTMLImageElement): boolean => {
  // 既に適用済みかつ alt が変わっていなければスキップ
  const prev = img.getAttribute(PROCESSED_ATTR);
  if (prev != null && prev === img.alt) return false;

  const parsed = parseAlt(img.alt);
  if (parsed == null) {
    // サイズマーカーが無い場合: 過去に適用していたら状態を戻す
    if (prev != null) {
      img.style.removeProperty('width');
      img.style.removeProperty('height');
      img.removeAttribute(PROCESSED_ATTR);
    }
    return false;
  }

  if (parsed.width != null) {
    img.style.setProperty('width', parsed.width, 'important');
  } else {
    img.style.removeProperty('width');
  }
  if (parsed.height != null) {
    img.style.setProperty('height', parsed.height, 'important');
  } else {
    img.style.removeProperty('height');
  }
  // 最大幅などの既存制約と競合した時に縮まないよう、max-width も解放
  img.style.setProperty('max-width', 'none', 'important');

  img.alt = parsed.cleanAlt;
  // 処理対象識別。次回 alt が変わっていれば再処理される。
  img.setAttribute(PROCESSED_ATTR, parsed.cleanAlt);
  return true;
};

const findScanRoots = (): HTMLElement[] => {
  const roots: HTMLElement[] = [];
  for (const selector of SCAN_ROOT_SELECTORS) {
    document.querySelectorAll<HTMLElement>(selector).forEach((el) => {
      if (!roots.includes(el)) roots.push(el);
    });
  }
  return roots;
};

let observer: MutationObserver | null = null;
let scanTimer: number | null = null;
let active = false;

const scan = (): void => {
  if (!active) return;
  let touched = 0;
  for (const root of findScanRoots()) {
    root.querySelectorAll<HTMLImageElement>('img').forEach((img) => {
      if (applyToImage(img)) touched++;
    });
  }
  if (touched > 0) {
    console.debug(`${LOG} updated ${touched} image(s)`);
  }
};

const scheduleScan = (): void => {
  if (scanTimer != null) return;
  scanTimer = window.setTimeout(() => {
    scanTimer = null;
    scan();
  }, 80);
};

const startObserver = (): void => {
  observer?.disconnect();
  if (document.body == null) return;
  observer = new MutationObserver((mutations) => {
    // img 関連の変化があった場合のみスキャン
    for (const m of mutations) {
      if (m.type === 'attributes' && m.target instanceof HTMLImageElement) {
        scheduleScan();
        return;
      }
      if (m.type === 'childList') {
        for (const node of m.addedNodes) {
          if (
            node instanceof HTMLImageElement ||
            (node instanceof HTMLElement && node.querySelector?.('img') != null)
          ) {
            scheduleScan();
            return;
          }
        }
      }
    }
  });
  observer.observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['alt', 'src'],
  });
};

const waitForBody = (cb: () => void): void => {
  if (document.body != null) {
    cb();
    return;
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', cb, { once: true });
    return;
  }
  const id = window.setInterval(() => {
    if (document.body != null) {
      window.clearInterval(id);
      cb();
    }
  }, 50);
};

export const mountImageSize = (): void => {
  active = true;
  console.info(`${LOG} activate (readyState=${document.readyState})`);
  waitForBody(() => {
    scan();
    startObserver();
  });
};

export const unmountImageSize = (): void => {
  active = false;
  if (scanTimer != null) {
    window.clearTimeout(scanTimer);
    scanTimer = null;
  }
  observer?.disconnect();
  observer = null;
  // 既に適用済みのスタイルは残しておく (alt は cleanAlt のまま)。
  // 完全に戻したい場合は deactivate より前にページをリロードする運用想定。
  console.info(`${LOG} deactivate`);
};
